/**
 * PartnerCompanyForm Component (T028 - US1)
 * Form for creating and editing partner companies
 *
 * Features:
 * - Create mode (empty form)
 * - Edit mode (populated with existing data)
 * - Validation for required name field
 * - URL validation for logoUrl
 * - Optional fields: kindeOrgId, defaultSsoRole, logoUrl
 * - isActive toggle (edit mode only)
 * - Loading state during submission
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { SessionCreditSection } from './care-concierge/SessionCreditSection';
import { CareConciergeToggle } from './care-concierge/CareConciergeToggle';
import { CareConciergeFeeField } from './care-concierge/CareConciergeFeeField';
import type {
  PartnerCompany,
  CreatePartnerCompanyPayload,
  UpdatePartnerCompanyPayload,
  UserRole,
} from '@/features/partner-companies/types/partner-company.types';

type PartnerCompanyFormProps = {
  partnerCompany?: PartnerCompany;
  onSubmit: (
    payload: CreatePartnerCompanyPayload | UpdatePartnerCompanyPayload,
  ) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
};

type FormData = {
  name: string;
  logoUrl: string;
  kindeOrgId: string;
  defaultSsoRole: UserRole | '';
  isActive: boolean;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

const ROLE_OPTIONS: Array<{ value: UserRole | ''; label: string }> = [
  { value: '', label: 'None' },
  { value: 'CARE_SEEKER', label: 'Care Seeker' },
  { value: 'SERVICE_PROVIDER', label: 'Service Provider' },
  { value: 'FINANCIAL_ADVISOR', label: 'Financial Advisor' },
];

/**
 * Validates URL format
 */
function isValidUrl(url: string): boolean {
  if (!url) return true; // Empty is valid (optional field)
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function PartnerCompanyForm({
  partnerCompany,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: PartnerCompanyFormProps) {
  const isEditMode = !!partnerCompany;

  const [formData, setFormData] = useState<FormData>({
    name: partnerCompany?.name || '',
    logoUrl: partnerCompany?.logoUrl || '',
    kindeOrgId: partnerCompany?.kindeOrgId || '',
    defaultSsoRole: partnerCompany?.defaultSsoRole || '',
    isActive: partnerCompany?.isActive ?? true,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Update form when partnerCompany prop changes
  useEffect(() => {
    if (partnerCompany) {
      setFormData({
        name: partnerCompany.name,
        logoUrl: partnerCompany.logoUrl || '',
        kindeOrgId: partnerCompany.kindeOrgId || '',
        defaultSsoRole: partnerCompany.defaultSsoRole || '',
        isActive: partnerCompany.isActive,
      });
    }
  }, [partnerCompany]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name is required
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Validate URL format if provided
    if (formData.logoUrl && !isValidUrl(formData.logoUrl)) {
      newErrors.logoUrl = 'Invalid URL format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditMode) {
        const payload: UpdatePartnerCompanyPayload = {
          name: formData.name.trim(),
          logoUrl: formData.logoUrl.trim() || null,
          kindeOrgId: formData.kindeOrgId.trim() || null,
          defaultSsoRole: formData.defaultSsoRole || null,
          isActive: formData.isActive,
        };
        await onSubmit(payload);
      } else {
        const payload: CreatePartnerCompanyPayload = {
          name: formData.name.trim(),
          ...(formData.logoUrl && { logoUrl: formData.logoUrl.trim() }),
          ...(formData.kindeOrgId && { kindeOrgId: formData.kindeOrgId.trim() }),
          ...(formData.defaultSsoRole && {
            defaultSsoRole: formData.defaultSsoRole,
          }),
        };
        await onSubmit(payload);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (
    field: keyof FormData,
    value: string | boolean | UserRole,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      {/* Feature 106 widened this from max-w-lg: the Care Concierge section adds a balance, an
          add-grant row and a ledger that do not fit the original width. The height cap and scroll
          keep a long grant history inside the viewport. */}
      <div
        role="dialog"
        aria-labelledby="form-title"
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="form-title" className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Partner Company' : 'Create Partner Company'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Referral Code (edit mode only, read-only) */}
          {isEditMode && partnerCompany && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Referral Code
              </label>
              <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700 font-mono">
                {partnerCompany.referralCode}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Auto-generated and cannot be changed
              </p>
            </div>
          )}

          {/* Name */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={isSubmitting}
              maxLength={255}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Logo URL */}
          <div>
            <label
              htmlFor="logoUrl"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Logo URL
            </label>
            <input
              id="logoUrl"
              type="url"
              value={formData.logoUrl}
              onChange={(e) => handleChange('logoUrl', e.target.value)}
              disabled={isSubmitting}
              placeholder="https://example.com/logo.png"
              maxLength={500}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                errors.logoUrl ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.logoUrl && (
              <p className="mt-1 text-sm text-red-600">{errors.logoUrl}</p>
            )}
          </div>

          {/* Kinde Org ID */}
          <div>
            <label
              htmlFor="kindeOrgId"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Kinde Org ID
            </label>
            <input
              id="kindeOrgId"
              type="text"
              value={formData.kindeOrgId}
              onChange={(e) => handleChange('kindeOrgId', e.target.value)}
              disabled={isSubmitting}
              placeholder="org_abc123"
              maxLength={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">
              For SSO integration (future use)
            </p>
          </div>

          {/* Default SSO Role */}
          <div>
            <label
              htmlFor="defaultSsoRole"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Default SSO Role
            </label>
            <select
              id="defaultSsoRole"
              value={formData.defaultSsoRole}
              onChange={(e) =>
                handleChange('defaultSsoRole', e.target.value as UserRole | '')
              }
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#026857] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Role assigned to users via SSO (future use)
            </p>
          </div>

          {/* Is Active (edit mode only) */}
          {isEditMode && (
            <div className="flex items-center gap-2">
              <input
                id="isActive"
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                disabled={isSubmitting}
                aria-label="Active"
                className="h-4 w-4 text-[#026857] border-gray-300 rounded focus:ring-[#026857] disabled:cursor-not-allowed"
              />
              <label
                htmlFor="isActive"
                className="text-sm font-medium text-gray-700"
              >
                Active
              </label>
            </div>
          )}

          {/* Care Concierge session credit (Feature 106) — edit mode only, because a company
              being created has no id yet to grant against. Action-based rather than form fields:
              the ledger is append-only and must never move as a side effect of saving the
              profile. Every control inside is type="button", so none of them submit this form. */}
          {isEditMode && partnerCompany && (
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Care Concierge Sessions
              </h3>
              <CareConciergeToggle owner="partner-company" ownerId={partnerCompany.id} />
              <CareConciergeFeeField owner="partner-company" ownerId={partnerCompany.id} />
              <SessionCreditSection ownerType="FIRM" ownerId={partnerCompany.id} />
            </div>
          )}

          {/* Form actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#026857] text-white rounded-md hover:bg-[#025246] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <span>{isEditMode ? 'Update' : 'Create'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
